import SocketIO, { Socket } from "socket.io-client";
import { AppManager } from ".";
import { BASE_URL } from "../constants/apiEndpoints";
import { INotification } from "../models/Notification/Notification";
import { TFullOrganizationAttr, TOrganizationAttr, TOrgInvitation } from "../models/Organization/Types";
import { ISimpleUser } from "../models/User/SimpleUserType";
import { TUserAttr } from "../models/User/User";
import { AppStoreActions } from "../redux/services/app/actions";

type NetworkData<T> = { data: T; source: ISimpleUser };

export type IOEventTypes = {
    NOTIFICATION: NetworkData<INotification>;

    USER_UPDATED: NetworkData<TUserAttr>;

    ORGANIZATION_UPDATED: NetworkData<TFullOrganizationAttr>;
    ORG_INVITE_CREATED: NetworkData<TOrgInvitation>;
    ORG_INVITE_UPDATED: NetworkData<TOrgInvitation>;
    ORG_INVITE_DELETED: NetworkData<TOrgInvitation>;
};

const ALL_EVENTS: (keyof IOEventTypes)[] = [
    "NOTIFICATION",

    // User events
    "USER_UPDATED",

    // Org events
    "ORGANIZATION_UPDATED",
    "ORG_INVITE_CREATED",
    "ORG_INVITE_UPDATED",
    "ORG_INVITE_DELETED",
];

type InitConfig = {
    onReAuthenticate: () => void;
    onReconnected: () => void;
    onDataReceived: <K extends keyof IOEventTypes>(evt: K, data: IOEventTypes[K]) => void;
};

export class RealtimeRequestManager {
    socket?: Socket;

    private config?: InitConfig;

    private socketListenersRegistered = false;

    init(config: InitConfig) {
        this.config = config;
        if (this.socket && this.socket.connected) {
            this.destroy();
        }
        this.socket = SocketIO(BASE_URL, {
            transports: ["websocket"],
            jsonp: false,
            reconnection: true,
            query: {
                token: AppManager.auth.getAccessToken() || "",
            },
        });
        this.socket.on("connect_error", (reason) => {
            if (reason.message === "UnAuthorizedSocketError") {
                this.socket!.disconnect();
                this.config!.onReAuthenticate();
            }
        });
        this.socket.io.on("reconnect_attempt", (attemptNumber) => {
            AppStoreActions.updateNetworState("reconnecting");
        });
        this.socket.io.on("reconnect_failed", () => {
            AppStoreActions.updateNetworState("offline");
        });
        this.socket.io.on("reconnect_error", () => {
            AppStoreActions.updateNetworState("offline");
        });
        this.socket.on("disconnect", () => {
            console.log("DISCONNECTED!");
            AppStoreActions.updateNetworState("offline");
        });
        this.socket.on("connect", () => {
            console.log("CONNECTED!");
            this.initSocketListeners();
            if (AppStoreActions.getNetworkState() === "reconnecting") {
                this.config!.onReconnected();
            }
            AppStoreActions.updateNetworState("online");
        });
    }

    private initSocketListeners() {
        if (this.socketListenersRegistered) return;
        this.socketListenersRegistered = true;
        ALL_EVENTS.forEach((EVENT_NAME) => {
            console.log("REGISTERING ", EVENT_NAME);
            this.socket?.on(EVENT_NAME, (data) => {
                this.config!.onDataReceived(EVENT_NAME, data);
            });
        });
    }

    destroy() {
        if (!this.socket?.disconnected) {
            this.socketListenersRegistered = false;
            this.socket?.disconnect();
        }
    }

    emitEvent(event: string, data: any) {
        if (this.socket?.connected) {
            this.socket?.emit(event, data);
        }
    }
}
